import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE, ARTIST_SECTION } from '../../constants/gaEnums';
import { artistApi } from '../../api/artist';
import PageLayout from '../../components/layout/PageLayout';
import ProgressBar from '../../components/common/ProgressBar';
import { Grid, Typography, Box, Stack, useMediaQuery } from '@mui/material';
import { useSearchParams, useLocation } from 'react-router-dom';
import ArtistCard from '../../components/card/ArtistCard';
import Pagination from '../../components/common/Pagination';
import PageTitle from '../../components/common/PageTitle';
import { useScrollToTopOnPageChange } from '../../hooks/useScrollToTopOnPageChange';
import SidebarLayout from '../../components/layout/SidebarLayout';
import theme from '../../theme';
import AlbumVerticalCard from '../../components/card/AlbumVerticalCard';
import InFeedAd from '../../components/ad/InFeedAd';

const ARTIST_SECTION_MAP = {
  'full-albums': ARTIST_SECTION.FULL_ALBUMS,
  singles: ARTIST_SECTION.SINGLES,
  'live-albums': ARTIST_SECTION.LIVE_ALBUMS,
  'compilation-albums': ARTIST_SECTION.COMPILATION_ALBUMS,
  'appears-on-albums': ARTIST_SECTION.APPEARS_ON,
  'similar-artists': ARTIST_SECTION.SIMILAR_ARTISTS,
};

const VIEW_TYPE_LABELS = {
  singles: '싱글 및 EP',
  'full-albums': '앨범',
  'live-albums': '라이브 앨범',
  'compilation-albums': '컴필레이션 앨범',
  'appears-on-albums': '피쳐링',
  'similar-artists': '유사한 아티스트',
};

const VIEW_TYPE_API_MAP = new Set([
  'full-albums',
  'singles',
  'live-albums',
  'compilation-albums',
  'appears-on-albums',
  'similar-artists',
]);

const ArtistViewAll = () => {
  const params = useParams();
  const { albumType } = params;
  const { state: locationState } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const artistName = locationState?.artistName || searchParams.get('artistName');
  const querySourceId = searchParams.get('source_id');
  const page = parseInt(searchParams.get('page') || '1', 10);

  // slug 기반 라우트(/artists/:artistSlug/view/:albumType)인 경우 sourceId를 다양한 경로로 확보
  const initialSourceId = params.sourceId || locationState?.sourceId || querySourceId || null;
  const initialArtistSource = params.artistSource || (initialSourceId ? 'apple_music' : null);

  const [artistSource, setArtistSource] = useState(initialArtistSource);
  const [sourceId, setSourceId] = useState(initialSourceId);
  const [items, setItems] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useTrackPageView(
    {
      pageType: PAGE_TYPE.ARTIST_VIEW_ALL,
      contentType: CONTENT_TYPE.ARTIST,
      contentId: sourceId,
      contentName: artistName,
      artistSection: ARTIST_SECTION_MAP[albumType],
    },
    { ready: !!sourceId },
  );

  const isNewViewType = VIEW_TYPE_API_MAP.has(albumType);
  const isSimilarArtists = albumType === 'similar-artists';

  // DB에 있는 slug 케이스: source_id 없이 slug만 있을 때 아티스트 조회로 sourceId 확보
  useEffect(() => {
    if (params.artistSlug && !sourceId) {
      artistApi
        .getArtistBySlug(params.artistSlug)
        .then((result) => {
          const artist = result.data.artist;
          setArtistSource(artist.artistSource.toLowerCase());
          setSourceId(artist.sourceId);
        })
        .catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const itemsPerRow = isXs ? 2 : 4;
  const itemsPerPage = isXs ? 20 : 40;
  const itemsPerAdInterval = itemsPerRow * 5; // 5줄마다 광고

  useScrollToTopOnPageChange();

  const totalPages = useMemo(() => {
    return Math.ceil(items.total / itemsPerPage);
  }, [items.total, itemsPerPage]);

  const safePage = totalPages > 0 ? Math.min(page, totalPages) : 1;

  const currentPageItems = useMemo(() => {
    const startIndex = (safePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.items.slice(startIndex, endIndex);
  }, [items.items, safePage, itemsPerPage]);

  useEffect(() => {
    if (!artistSource || !sourceId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        let data;
        if (isNewViewType) {
          const response = await artistApi.getArtistViewByType(artistSource, sourceId, albumType);
          data = response.data;
        } else {
          const response = await artistApi.getAlbumsByType(artistSource, sourceId, albumType);
          data = response.data;
        }
        const normalizedData = {
          total: data.total || data.items?.length || 0,
          items: data.items || data || [],
        };
        setItems(normalizedData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setItems({ total: 0, items: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [artistSource, sourceId, albumType, isNewViewType]);

  const handlePageChange = (_, newPage) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    setSearchParams(newSearchParams);
  };

  const label = VIEW_TYPE_LABELS[albumType] || albumType;

  return (
    <PageLayout>
      <ProgressBar isLoading={loading}>
        <Stack spacing={{ xs: 2, md: 2.5, lg: 3.75 }}>
          <Stack spacing={1}>
            <Typography fontSize={{ xs: 12, md: 14, lg: 16 }} color={theme.palette.secondary.main}>
              {label}
            </Typography>
            <PageTitle>{artistName || ''}</PageTitle>
          </Stack>
          <SidebarLayout pageType={PAGE_TYPE.ARTIST_VIEW_ALL}>
            <Box sx={{ overflow: 'hidden' }}>
              <Grid
                container
                rowSpacing={{ xs: 3, md: 5, lg: 5 }}
                columnSpacing={{ xs: 2, md: 2, lg: 2.5 }}
              >
                {currentPageItems.length > 0 ? (
                  currentPageItems.flatMap((item, index) => {
                    const elements = [];
                    const position = index + 1;

                    elements.push(
                      isSimilarArtists ? (
                        <Grid item xs={6} md={3} key={`${item.id}-${page}-${index}`}>
                          <ArtistCard
                            id={item.id}
                            artistDbId={item.artistId}
                            name={item.name}
                            artistImageUrl={item.artistImageUrl}
                            artistSource={item.artistSource}
                            slug={item.slug}
                            artistSection={ARTIST_SECTION_MAP[albumType]}
                            pageType={PAGE_TYPE.ARTIST_VIEW_ALL}
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={6} md={3} key={`${item.sourceId}-${page}-${index}`}>
                          <AlbumVerticalCard
                            albumId={item.albumId}
                            sourceId={item.sourceId}
                            albumSource={item.albumSource}
                            slug={item.slug}
                            imageUrl={item.albumImageUrl}
                            albumName={item.title}
                            artistId={item.artist?.sourceId}
                            artistSource={item.artist?.artistSource}
                            artistName={item.artist?.name}
                            artistSlug={item.artist?.slug}
                            albumAvgRating={item.averageRating}
                            commentCount={item.commentCount}
                            bookmarkCount={item.bookmarkCount}
                            releaseDate={item.releaseDate}
                            isStarred={item.isStarred}
                            contentRating={item.contentRating}
                            hideBookmarkCount
                            hideArtist
                            ratingSolo={!isLg}
                            dateSolo={isLg}
                            artistSection={ARTIST_SECTION_MAP[albumType]}
                            pageType={PAGE_TYPE.ARTIST_VIEW_ALL}
                          />
                        </Grid>
                      ),
                    );

                    if (position % itemsPerAdInterval === 0 && position < currentPageItems.length) {
                      elements.push(
                        <Grid item xs={12} key={`ad-${page}-${index}`}>
                          <InFeedAd pageType={PAGE_TYPE.ARTIST} />
                        </Grid>,
                      );
                    }

                    return elements;
                  })
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, width: '100%' }}>
                    <Typography variant="body1" color="text.secondary">
                      {loading
                        ? '로딩 중...'
                        : isSimilarArtists
                          ? '아티스트가 없습니다.'
                          : '앨범이 없습니다.'}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Box>
          </SidebarLayout>
        </Stack>

        {totalPages > 1 && (
          <Pagination count={totalPages} page={safePage} onChange={handlePageChange} />
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default ArtistViewAll;
